import { useCallback, useEffect, useRef, useState } from "react";
import useUser from "../../context/userHook";
import useVisualIndicator from "./useVisualIndicator";
import useVisualIndicatorStream from "../../services/useVisualIndicatorStream";
import logger from "../../../lib/logger";
import type { VisualIndicatorJobStatus } from "../../../components/Navbar/Settings/types/settings-drawer-interface";

export const VISUAL_INDICATOR_POLL_MS = 10_000;

/**
 * How long a freshly started request may answer `{ status: "unknown" }` before the banner gives up.
 * Orchestration writes the request's state when it fans out, which can lag the 202 a little.
 */
export const VISUAL_INDICATOR_UNKNOWN_GRACE_MS = 120_000;

/** What the banner says about the request; known when it was started from this page. */
export interface VisualIndicatorJobInfo {
  mode?: "swap" | "remove";
  /** The indicator being replaced or removed, as displayed ("✨"). */
  from?: string;
  /** The new indicator, as displayed ("🔗"). Swap only. */
  to?: string;
}

export interface VisualIndicatorJobView {
  requestId: string;
  status: VisualIndicatorJobStatus;
  info: VisualIndicatorJobInfo;
}

interface JobState {
  requestId: string | null;
  status: VisualIndicatorJobStatus | null;
  info: VisualIndicatorJobInfo;
  visible: boolean;
  trackedAt: number;
}

const EMPTY: JobState = { requestId: null, status: null, info: {}, visible: false, trackedAt: 0 };

const zeroStatus = (requestId: string, totalItems: number): VisualIndicatorJobStatus => ({
  requestId,
  status: "running",
  totalItems,
  processedItems: 0,
  renamed: 0,
  skipped: { renamedByUser: 0, archived: 0, noAccess: 0, unchanged: 0 },
  failed: 0,
});

/**
 * The socket and the 10 s poll race each other, so keep whichever reading has progressed further,
 * and never let a late "running" undo a terminal state.
 */
export const mergeJobStatus = (
  prev: VisualIndicatorJobStatus | null,
  next: VisualIndicatorJobStatus,
): VisualIndicatorJobStatus => {
  if (!prev) return next;
  const counts = (next.processedItems ?? 0) >= (prev.processedItems ?? 0) ? next : prev;
  const status = next.status === "running" && prev.status !== "running" ? prev.status : next.status;
  return { ...counts, skipped: { ...prev.skipped, ...counts.skipped }, status };
};

const isKnown = (result: any): result is VisualIndicatorJobStatus =>
  Boolean(result) && result.status !== "unknown" && typeof result.status === "string";

interface UseVisualIndicatorJobArgs {
  /** AccountSettings.visual_indicator_job.requestId: checked once on mount. */
  initialRequestId?: string | null;
  /** What to say about a request found on mount, which this page did not start. */
  infoForResumed?: () => VisualIndicatorJobInfo;
}

/**
 * Tracks the account's bulk visual indicator rename for the banner: progress from the socket,
 * the status endpoint on mount (the account's latest request) and every 10 s while it runs.
 */
const useVisualIndicatorJob = ({ initialRequestId, infoForResumed }: UseVisualIndicatorJobArgs = {}) => {
  const { userId, accountId } = useUser();
  const { status: fetchStatus, stop: stopRequest, ready } = useVisualIndicator();
  const [job, setJob] = useState<JobState>(EMPTY);
  const jobRef = useRef(job);
  jobRef.current = job;
  const infoForResumedRef = useRef(infoForResumed);
  infoForResumedRef.current = infoForResumed;

  const merge = useCallback((incoming: VisualIndicatorJobStatus) => {
    setJob((prev) =>
      prev.requestId && incoming?.requestId === prev.requestId
        ? { ...prev, status: mergeJobStatus(prev.status, incoming) }
        : prev,
    );
  }, []);

  const refresh = useCallback(async () => {
    const { requestId, trackedAt } = jobRef.current;
    if (!requestId) return;
    try {
      const result = await fetchStatus(requestId);
      if (isKnown(result)) {
        merge({ ...result, requestId });
      } else if (Date.now() - trackedAt > VISUAL_INDICATOR_UNKNOWN_GRACE_MS) {
        // The request's state has expired; nothing left to report.
        setJob((prev) => (prev.requestId === requestId ? EMPTY : prev));
      }
    } catch (error: any) {
      logger.error("Error reading visual indicator status:", error);
    }
  }, [fetchStatus, merge]);

  // The account's latest request, once: show the banner only if it is still running.
  const checkedRequestId = useRef<string | null>(null);
  useEffect(() => {
    if (!ready || !initialRequestId || checkedRequestId.current === initialRequestId) return;
    checkedRequestId.current = initialRequestId;
    if (jobRef.current.requestId) return;
    let cancelled = false;
    (async () => {
      try {
        const result = await fetchStatus(initialRequestId);
        if (cancelled || jobRef.current.requestId || !isKnown(result) || result.status !== "running") return;
        setJob({
          requestId: initialRequestId,
          status: { ...result, requestId: initialRequestId },
          info: infoForResumedRef.current?.() ?? {},
          visible: true,
          trackedAt: Date.now(),
        });
      } catch (error: any) {
        logger.error("Error reading visual indicator status:", error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ready, initialRequestId, fetchStatus]);

  const running = job.status?.status === "running";
  useEffect(() => {
    if (!job.requestId || !running) return;
    const timer = setInterval(refresh, VISUAL_INDICATOR_POLL_MS);
    return () => clearInterval(timer);
  }, [job.requestId, running, refresh]);

  useVisualIndicatorStream({ userId, accountId, onEvent: merge, onReconnect: refresh });

  /** Start following a request this page just queued. */
  const track = useCallback((requestId: string, info: VisualIndicatorJobInfo, estimatedItems = 0) => {
    checkedRequestId.current = requestId;
    setJob({ requestId, status: zeroStatus(requestId, estimatedItems), info, visible: true, trackedAt: Date.now() });
  }, []);

  const stop = useCallback(async () => {
    const { requestId } = jobRef.current;
    if (!requestId) return;
    await stopRequest(requestId);
    setJob((prev) =>
      prev.requestId === requestId && prev.status
        ? { ...prev, status: { ...prev.status, status: prev.status.status === "running" ? "stopped" : prev.status.status } }
        : prev,
    );
  }, [stopRequest]);

  const dismiss = useCallback(() => setJob((prev) => ({ ...prev, visible: false })), []);

  const view: VisualIndicatorJobView | null =
    job.visible && job.requestId && job.status ? { requestId: job.requestId, status: job.status, info: job.info } : null;

  return { job: view, track, stop, dismiss };
};

export default useVisualIndicatorJob;
